'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdAdd, MdCampaign, MdDelete, MdPublish, MdVisibility } from 'react-icons/md'
import { toast } from 'react-toastify'

import ConfirmModal from '@/components/confirm-modal'
import {
    createSystemAnnouncement,
    deleteSystemAnnouncement,
    getAnnouncementReaders,
    getSystemAnnouncements,
    publishSystemAnnouncement,
    type SystemAnnouncement,
    type SystemAnnouncementKind,
    type SystemAnnouncementReader,
} from '@/lib/api/system-announcements'

/**
 * Publicação de avisos do sistema.
 *
 * Substitui o grupo de WhatsApp como canal de aviso: o que sai daqui aparece na
 * tela de quem está online e, para quem não está, na próxima vez que entrar —
 * é o recibo de leitura por usuário que garante isso.
 *
 * O rascunho existe para separar escrever de publicar: depois de publicado,
 * quem já leu NÃO lê de novo, então corrigir o texto não alcança essas pessoas.
 */

const KIND_LABEL: Record<SystemAnnouncementKind, string> = {
    INFO: 'Comunicado',
    WARNING: 'Atenção',
    MAINTENANCE: 'Manutenção',
    RELEASE: 'Novidades',
}

const KIND_BADGE: Record<SystemAnnouncementKind, string> = {
    INFO: 'badge-info',
    WARNING: 'badge-warning',
    MAINTENANCE: 'badge-warning',
    RELEASE: 'badge-secondary',
}

/** Sugestão de janela: 14 dias. Ver o aviso sob o campo. */
const DEFAULT_WINDOW_DAYS = 14

/** `yyyy-MM-dd` do dia local — o formato que o `<input type="date">` espera. */
const toDateInput = (date: Date): string => {
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * O término é uma DATA, e vale até o fim dela.
 *
 * O campo era `datetime-local` e isso produziu o primeiro aviso natimorto: quem escolheu
 * "30 de setembro" mudou só a data e o campo manteve a hora que veio no padrão — a hora
 * em que o formulário foi aberto. O aviso passou a terminar às 10:25 e foi publicado às
 * 10:28, três minutos depois do próprio fim.
 *
 * Ninguém pensa em término de aviso com hora: "vale até dia 30" quer dizer o dia 30
 * inteiro. O horário sai do fuso de quem publica (`23:59:59` local vira o instante certo
 * em UTC no `toISOString`), que é o que a pessoa tem em mente ao escolher.
 */
const endOfDayIso = (dateInput: string): string => {
    const [year, month, day] = dateInput.split('-').map(Number)
    return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString()
}

export default function SystemAnnouncementsPage() {
    const [items, setItems] = useState<SystemAnnouncement[]>([])
    const [loading, setLoading] = useState(true)

    const [showForm, setShowForm] = useState(false)
    const [title, setTitle] = useState('')
    const [body, setBody] = useState('')
    const [kind, setKind] = useState<SystemAnnouncementKind>('INFO')
    const [requiresAck, setRequiresAck] = useState(true)
    const [endsAt, setEndsAt] = useState('')
    const [saving, setSaving] = useState(false)

    const [toPublish, setToPublish] = useState<SystemAnnouncement | null>(null)
    const [toDelete, setToDelete] = useState<SystemAnnouncement | null>(null)
    const [readersOf, setReadersOf] = useState<SystemAnnouncement | null>(null)
    const [readers, setReaders] = useState<SystemAnnouncementReader[]>([])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getSystemAnnouncements()
            if (response.success) setItems(response.result ?? [])
            else toast.error(response.error || 'Não foi possível carregar os avisos')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const resetForm = () => {
        setTitle('')
        setBody('')
        setKind('INFO')
        setRequiresAck(true)
        const suggestion = new Date()
        suggestion.setDate(suggestion.getDate() + DEFAULT_WINDOW_DAYS)
        setEndsAt(toDateInput(suggestion))
    }

    const openForm = () => {
        resetForm()
        setShowForm(true)
    }

    /**
     * Aviso que termina no passado não aparece para ninguém — a consulta de pendentes
     * exige `endsAt > agora`. Aconteceu no primeiro uso real: o aviso 1 foi publicado
     * com término três minutos antes, e a tela ainda disse "passa a aparecer para todo
     * mundo". Agora o botão de publicar nem fica disponível.
     */
    // Compara com o FIM do dia escolhido: escolher hoje é válido, e vale até 23:59.
    const endsInThePast = !!endsAt && new Date(endOfDayIso(endsAt)).getTime() <= Date.now()

    const save = async (publish: boolean) => {
        if (title.trim().length < 3 || body.trim().length < 3) {
            toast.error('Preencha o título e o conteúdo do aviso')
            return
        }
        if (publish && endsInThePast) {
            toast.error(
                'A data de término já passou: este aviso não apareceria para ninguém.'
            )
            return
        }
        setSaving(true)
        try {
            const response = await createSystemAnnouncement({
                title: title.trim(),
                body: body.trim(),
                kind,
                requiresAck,
                endsAt: endsAt ? endOfDayIso(endsAt) : null,
                publish,
            })
            if (response.success) {
                toast.success(publish ? 'Aviso publicado' : 'Rascunho salvo')
                setShowForm(false)
                void load()
            } else {
                toast.error(response.error || 'Não foi possível salvar o aviso')
            }
        } finally {
            setSaving(false)
        }
    }

    const confirmPublish = async () => {
        if (!toPublish) return
        const response = await publishSystemAnnouncement(toPublish.id)
        if (response.success) {
            toast.success('Aviso publicado')
            void load()
        } else {
            toast.error(response.error || 'Não foi possível publicar')
        }
        setToPublish(null)
    }

    const confirmDelete = async () => {
        if (!toDelete) return
        const response = await deleteSystemAnnouncement(toDelete.id)
        if (response.success) {
            toast.success('Aviso retirado de circulação')
            void load()
        } else {
            toast.error(response.error || 'Não foi possível remover')
        }
        setToDelete(null)
    }

    const openReaders = async (announcement: SystemAnnouncement) => {
        setReadersOf(announcement)
        setReaders([])
        const response = await getAnnouncementReaders(announcement.id)
        if (response.success) setReaders(response.result ?? [])
    }

    const drafts = useMemo(() => items.filter(item => !item.publishedAt), [items])
    const published = useMemo(() => items.filter(item => item.publishedAt), [items])

    const renderRow = (item: SystemAnnouncement) => (
        <tr key={item.id}>
            <td>
                <div className="font-medium">{item.title}</div>
                <div className="text-xs text-base-content/60 line-clamp-2 max-w-md whitespace-pre-wrap">
                    {item.body}
                </div>
            </td>
            <td>
                <span className={`badge badge-sm ${KIND_BADGE[item.kind]}`}>
                    {KIND_LABEL[item.kind]}
                </span>
            </td>
            <td className="text-sm">
                {item.publishedAt ? (
                    new Date(item.publishedAt).toLocaleString('pt-BR')
                ) : (
                    <span className="text-base-content/50">rascunho</span>
                )}
            </td>
            <td className="text-sm">
                {item.endsAt ? (
                    // Vencido é o estado que mais engana: o aviso aparece publicado na
                    // lista e não alcança ninguém.
                    new Date(item.endsAt).getTime() <= Date.now() ? (
                        <span className="text-error font-medium">
                            {new Date(item.endsAt).toLocaleDateString('pt-BR')} · vencido
                        </span>
                    ) : (
                        new Date(item.endsAt).toLocaleDateString('pt-BR')
                    )
                ) : (
                    <span className="text-warning">sem término</span>
                )}
            </td>
            <td className="text-sm">{item._count?.reads ?? 0}</td>
            <td>
                <div className="flex items-center gap-1 justify-end">
                    {item.publishedAt && (
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs gap-1"
                            onClick={() => void openReaders(item)}
                        >
                            <MdVisibility /> Leitores
                        </button>
                    )}
                    {!item.publishedAt && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-xs gap-1"
                            onClick={() => setToPublish(item)}
                        >
                            <MdPublish /> Publicar
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => setToDelete(item)}
                    >
                        <MdDelete />
                    </button>
                </div>
            </td>
        </tr>
    )

    const renderTable = (rows: SystemAnnouncement[]) => (
        <div className="overflow-x-auto">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>Aviso</th>
                        <th>Tipo</th>
                        <th>Publicado</th>
                        <th>Término</th>
                        <th>Leituras</th>
                        <th />
                    </tr>
                </thead>
                <tbody>{rows.map(renderRow)}</tbody>
            </table>
        </div>
    )

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdCampaign className="text-secondary" /> Avisos do sistema
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1 max-w-2xl">
                        O que sai daqui aparece na tela de quem está online e, para quem não
                        está, na próxima vez que entrar. Cada pessoa vê uma vez.
                    </p>
                </div>
                <button type="button" className="btn btn-secondary gap-2" onClick={openForm}>
                    <MdAdd className="text-lg" /> Novo aviso
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 text-base-content/50">Nenhum aviso ainda.</div>
            ) : (
                <div className="space-y-8">
                    {drafts.length > 0 && (
                        <section>
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                                Rascunhos
                            </h2>
                            {renderTable(drafts)}
                        </section>
                    )}

                    <section>
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                            Publicados
                        </h2>
                        {published.length === 0 ? (
                            <p className="text-sm text-base-content/50">Nada publicado ainda.</p>
                        ) : (
                            renderTable(published)
                        )}
                    </section>
                </div>
            )}

            {showForm && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-2xl">
                        <h3 className="text-lg font-bold mb-4">Novo aviso</h3>

                        <div className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label py-1" htmlFor="announcement-title">
                                    <span className="label-text text-sm font-medium">Título</span>
                                </label>
                                <input
                                    id="announcement-title"
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    maxLength={120}
                                    placeholder="Manutenção programada para sábado"
                                    disabled={saving}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1" htmlFor="announcement-body">
                                    <span className="label-text text-sm font-medium">Conteúdo</span>
                                </label>
                                <textarea
                                    id="announcement-body"
                                    className="textarea textarea-bordered w-full min-h-40 leading-relaxed"
                                    value={body}
                                    onChange={e => setBody(e.target.value)}
                                    placeholder="O que mudou, o que a equipe precisa fazer, e a partir de quando."
                                    disabled={saving}
                                />
                                <span className="text-xs text-base-content/50 mt-1">
                                    As quebras de linha aparecem como você escrever.
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label py-1" htmlFor="announcement-kind">
                                        <span className="label-text text-sm font-medium">Tipo</span>
                                    </label>
                                    <select
                                        id="announcement-kind"
                                        className="select select-bordered w-full"
                                        value={kind}
                                        onChange={e =>
                                            setKind(e.target.value as SystemAnnouncementKind)
                                        }
                                        disabled={saving}
                                    >
                                        {(Object.keys(KIND_LABEL) as SystemAnnouncementKind[]).map(
                                            value => (
                                                <option key={value} value={value}>
                                                    {KIND_LABEL[value]}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label py-1" htmlFor="announcement-ends">
                                        <span className="label-text text-sm font-medium">
                                            Aparece até o dia
                                        </span>
                                    </label>
                                    <input
                                        id="announcement-ends"
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={endsAt}
                                        min={toDateInput(new Date())}
                                        onChange={e => setEndsAt(e.target.value)}
                                        disabled={saving}
                                    />
                                    <span
                                        className={`text-xs mt-1 ${
                                            endsInThePast
                                                ? 'text-error font-medium'
                                                : 'text-base-content/50'
                                        }`}
                                    >
                                        {endsInThePast
                                            ? 'Esta data já passou — o aviso não apareceria para ninguém.'
                                            : endsAt
                                              ? `Aparece até o fim do dia ${new Date(
                                                    endOfDayIso(endsAt)
                                                ).toLocaleDateString('pt-BR')}.`
                                              : 'Sem término, quem volta de férias recebe uma pilha de avisos de uma vez.'}
                                    </span>
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label
                                    className="label cursor-pointer justify-start gap-3 py-2"
                                    htmlFor="announcement-ack"
                                >
                                    <input
                                        id="announcement-ack"
                                        type="checkbox"
                                        className="checkbox checkbox-secondary checkbox-sm"
                                        checked={requiresAck}
                                        onChange={e => setRequiresAck(e.target.checked)}
                                        disabled={saving}
                                    />
                                    <span className="label-text text-sm font-medium">
                                        Exigir confirmação de leitura
                                    </span>
                                </label>
                                <p className="text-xs text-base-content/50 pl-9">
                                    A pessoa precisa clicar em &quot;Entendi&quot;, e você consegue
                                    ver quem já leu.
                                </p>
                            </div>
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setShowForm(false)}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => void save(false)}
                                disabled={saving}
                            >
                                Salvar rascunho
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary gap-2"
                                onClick={() => void save(true)}
                                disabled={saving || endsInThePast}
                                title={
                                    endsInThePast
                                        ? 'A data de término já passou'
                                        : undefined
                                }
                            >
                                {saving ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdPublish className="text-lg" />
                                )}
                                Publicar agora
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {readersOf && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-lg">
                        <h3 className="text-lg font-bold mb-1">Quem já leu</h3>
                        <p className="text-sm text-base-content/60 mb-4 truncate">
                            {readersOf.title}
                        </p>
                        {readers.length === 0 ? (
                            <p className="text-sm text-base-content/50 py-4">
                                Ninguém leu este aviso ainda.
                            </p>
                        ) : (
                            <ul className="divide-y divide-base-300 max-h-80 overflow-y-auto">
                                {readers.map(reader => (
                                    <li
                                        key={reader.user.id}
                                        className="py-2 flex items-center justify-between gap-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">
                                                {reader.user.name}
                                            </div>
                                            <div className="text-xs text-base-content/50 truncate">
                                                {reader.user.email}
                                            </div>
                                        </div>
                                        <span className="text-xs text-base-content/50 flex-shrink-0">
                                            {new Date(reader.readAt).toLocaleString('pt-BR')}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setReadersOf(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            <ConfirmModal
                isOpen={!!toPublish}
                onClose={() => setToPublish(null)}
                onConfirm={confirmPublish}
                title="Publicar este aviso?"
                message={
                    'Ele passa a aparecer para todo mundo — na tela de quem está online e, ' +
                    'para os demais, na próxima vez que entrarem. Depois de publicado, quem ' +
                    'já leu não lê de novo: corrigir o texto não alcança essas pessoas.'
                }
                confirmButtonText="Publicar"
            />

            <ConfirmModal
                isOpen={!!toDelete}
                onClose={() => setToDelete(null)}
                onConfirm={confirmDelete}
                title="Tirar de circulação?"
                message={
                    'O aviso deixa de aparecer para quem ainda não viu. O registro de quem já ' +
                    'leu continua existindo.'
                }
                confirmButtonText="Retirar"
            />
        </div>
    )
}
